import { motion } from 'framer-motion'
import { useState } from 'react'

interface ClientLockupProps {
  name: string
  /** The client's own logo. Null when they have not uploaded one. */
  logoUrl: string | null
  /** Sized to sit inside the login drawer rather than head a page. */
  compact?: boolean
}

/**
 * The client's mark over a "Powered by Up App" line, for the pre-auth screens.
 *
 * The customer got here from the client's outreach and has often never heard of Up, so
 * the screen belongs to the client outright: their mark carries it at a size meant to
 * be read at a glance, and Up appears once, in words, as the platform underneath. Their
 * name is the fallback whenever their logo is missing or fails to load; never Up's
 * default mark, which would read as the theatre's own and make the page less
 * trustworthy rather than more.
 *
 * The logo is rendered exactly as supplied — no tile, no background, no crop. A client
 * whose asset carries its own coloured panel keeps it; one supplying a bare transparent
 * mark stays bare. Inventing a surface to sit it on would mean guessing a colour their
 * logo may be unreadable against, which is the one thing this component must not do.
 */
export const ClientLockup = ({
  name,
  logoUrl,
  compact = false,
}: ClientLockupProps) => {
  const [logoFailed, setLogoFailed] = useState(false)

  return (
    <motion.div
      className={
        compact
          ? 'flex flex-col items-center gap-2 mt-4'
          : 'flex flex-col items-center gap-4'
      }
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      data-testid="client-lockup"
    >
      {logoUrl && !logoFailed ? (
        <img
          src={logoUrl}
          alt={name}
          onError={() => setLogoFailed(true)}
          // Bounded on both axes: a square mark fills the box as in the design,
          // while a wide one is held to the same height instead of running the
          // full width of the screen.
          className={
            compact
              ? 'h-auto w-[30vw] max-w-[120px] max-h-[120px] object-contain drop-shadow-md rounded-lg'
              : 'h-auto w-[52vw] max-w-[220px] max-h-[220px] object-contain drop-shadow-md rounded-lg'
          }
          data-testid="client-logo"
        />
      ) : (
        <span
          className={
            compact
              ? 'text-lg font-semibold leading-tight tracking-tight text-white'
              : 'text-2xl font-semibold leading-tight tracking-tight text-white'
          }
          data-testid="client-name"
        >
          {name}
        </span>
      )}
      <span
        className={compact ? 'text-xs text-purple5' : 'text-sm text-purple5'}
        data-testid="powered-by-up"
      >
        {`Powered by Up App`}
      </span>
    </motion.div>
  )
}
